import api from './client';

export type InquiryStatus = 'new' | 'contacted' | 'converted';

export interface InquiryListing {
  id: number;
  title: string;
  address: string;
  type: 'Rental' | 'Sale';
  price: string | number;
  coverPhoto: string | null;
}

export interface InquiryAgent {
  id: number;
  name: string | null;
  email: string;
}

export interface InquiryUser {
  id: number;
  name: string | null;
  email: string;
}

export interface Inquiry {
  id: number;
  listingId: number;
  agentId: number | null;
  userId: number | null;
  customerName: string;
  email: string;
  phone: string | null;
  message: string | null;
  status: InquiryStatus;
  createdAt: string;
  updatedAt: string;
  listing?: InquiryListing | null;
  agent?: InquiryAgent | null;
  user?: InquiryUser | null;
}

export interface InquiryStatusCounts {
  all: number;
  new: number;
  contacted: number;
  converted: number;
}

export interface InquiryListParams {
  status?: InquiryStatus;
  search?: string;
  listingId?: number;
  agentId?: number;
  userId?: number;
  /** YYYY-MM-DD, inclusive — filters on the date the inquiry was received. */
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export interface InquiryListResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  counts: InquiryStatusCounts;
  inquiries: Inquiry[];
}

interface RawListResponse {
  status: number;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  counts: InquiryStatusCounts;
  inquiries: Inquiry[];
}

interface MutationResponse {
  status: number;
  message?: string;
  error?: string | string[];
  inquiry?: Inquiry;
}

/** GET /api/inquiries */
export const listInquiries = async (
  params: InquiryListParams = {}
): Promise<InquiryListResponse> => {
  const query: Record<string, string> = {};
  if (params.status) query.status = params.status;
  if (params.search) query.search = params.search;
  if (params.listingId !== undefined) query.listingId = String(params.listingId);
  if (params.agentId !== undefined) query.agentId = String(params.agentId);
  if (params.userId !== undefined) query.userId = String(params.userId);
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTo) query.dateTo = params.dateTo;
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const res = await api.get<RawListResponse>('/inquiries', { params: query });
  return {
    total: res.data.total,
    page: res.data.page,
    limit: res.data.limit,
    totalPages: res.data.totalPages,
    counts: res.data.counts,
    inquiries: res.data.inquiries,
  };
};

/** PATCH /api/inquiries/:id/status */
export const setInquiryStatus = async (
  id: number,
  status: InquiryStatus
): Promise<Inquiry> => {
  const res = await api.patch<MutationResponse>(`/inquiries/${id}/status`, {
    status,
  });
  if (!res.data.inquiry) {
    throw new Error(
      typeof res.data.error === 'string'
        ? res.data.error
        : 'Failed to update inquiry'
    );
  }
  return res.data.inquiry;
};

/** DELETE /api/inquiries/:id  (admin only) */
export const deleteInquiry = async (id: number): Promise<void> => {
  await api.delete<MutationResponse>(`/inquiries/${id}`);
};

/** POST /api/inquiries  (public — used on the user-side listing detail page) */
export interface InquiryCreateInput {
  listingId: number;
  customerName: string;
  email: string;
  phone?: string;
  message?: string;
}
export const createInquiry = async (
  payload: InquiryCreateInput
): Promise<Inquiry> => {
  const res = await api.post<MutationResponse>('/inquiries', payload);
  if (!res.data.inquiry) {
    throw new Error(
      typeof res.data.error === 'string'
        ? res.data.error
        : 'Failed to submit inquiry'
    );
  }
  return res.data.inquiry;
};
